import { getWebview, NATIVE_APP_EVENT } from "@/lib/app-store-utils"
import {
  NativeEventHandler,
  NativeInvokeOptions,
  NativeRpcErrorPayload
} from "@/sdk/types"

const RPC_TYPES = {
  INVOKE: "rpc-invoke",
  RESULT: "rpc-result",
  EVENT: "rpc-event"
} as const

const DEFAULT_TIMEOUT_MS = 15000

type PendingNativeCall = {
  resolve: (data: unknown) => void
  reject: (error: Error) => void
  timeout: ReturnType<typeof setTimeout>
}

type NativeRpcResultEnvelope =
  | {
      type: "rpc-result"
      id: string
      ok: true
      data?: unknown
    }
  | {
      type: "rpc-result"
      id: string
      ok: false
      error?: NativeRpcErrorPayload
    }

type NativeRpcEventEnvelope = {
  type: "rpc-event"
  channel: string
  data?: unknown
}

function nativeError(code: string, message: string): Error {
  const error = new Error(message) as Error & { code?: string }
  error.code = code
  return error
}

function errorFromPayload(error?: NativeRpcErrorPayload): Error {
  return nativeError(
    error?.code ?? "native_error",
    error?.message ?? "Native RPC failed"
  )
}

function id(): string {
  const crypto = globalThis.crypto as
    | (Crypto & { randomUUID?: () => string })
    | undefined
  if (crypto?.randomUUID) return crypto.randomUUID()

  return `${Date.now()}-${Math.random()}`
}

export class NativeBridge {
  private readonly pending = new Map<string, PendingNativeCall>()
  private readonly handlers = new Map<string, Set<NativeEventHandler>>()

  constructor() {
    window.addEventListener(NATIVE_APP_EVENT, this.handleNativeMessage)
  }

  get available(): boolean {
    return !!getWebview()?.postMessage
  }

  invoke<T = unknown>(
    command: string,
    args: Record<string, unknown> = {},
    { timeoutMs = DEFAULT_TIMEOUT_MS }: NativeInvokeOptions = {}
  ): Promise<T> {
    const webview = getWebview()
    if (!webview?.postMessage) {
      return Promise.reject(
        nativeError(
          "bridge_unavailable",
          "Native WebView bridge is unavailable"
        )
      )
    }

    const requestId = id()

    return new Promise<T>((resolve, reject) => {
      const timeout = setTimeout(() => {
        this.pending.delete(requestId)
        reject(nativeError("timeout", `Native RPC timed out: ${command}`))
      }, timeoutMs)

      this.pending.set(requestId, {
        resolve: resolve as (data: unknown) => void,
        reject,
        timeout
      })

      try {
        webview.postMessage(
          JSON.stringify({
            type: RPC_TYPES.INVOKE,
            id: requestId,
            command,
            args
          })
        )
      } catch (error) {
        this.rejectPending(
          requestId,
          error instanceof Error
            ? error
            : nativeError("post_message_failed", "Native RPC message failed")
        )
      }
    })
  }

  on(channel: string, callback: NativeEventHandler): () => void {
    const handlers = this.handlers.get(channel) ?? new Set<NativeEventHandler>()
    handlers.add(callback)
    this.handlers.set(channel, handlers)

    return () => this.off(channel, callback)
  }

  off(channel: string, callback?: NativeEventHandler): void {
    if (!callback) {
      this.handlers.delete(channel)
      return
    }

    const handlers = this.handlers.get(channel)
    if (!handlers) return

    handlers.delete(callback)
    if (handlers.size === 0) this.handlers.delete(channel)
  }

  destroy(): void {
    window.removeEventListener(NATIVE_APP_EVENT, this.handleNativeMessage)

    for (const [requestId] of this.pending) {
      this.rejectPending(
        requestId,
        nativeError("destroyed", "Native bridge was destroyed")
      )
    }

    this.handlers.clear()
  }

  private readonly handleNativeMessage = (event: Event): void => {
    const detail = (event as CustomEvent).detail
    if (!detail || typeof detail !== "object") return

    if (this.isResult(detail)) {
      this.handleResult(detail)
    } else if (this.isEvent(detail)) {
      this.handleEvent(detail)
    }
  }

  private isResult(
    detail: Record<string, unknown>
  ): detail is NativeRpcResultEnvelope {
    return detail.type === RPC_TYPES.RESULT && typeof detail.id === "string"
  }

  private isEvent(
    detail: Record<string, unknown>
  ): detail is NativeRpcEventEnvelope {
    return detail.type === RPC_TYPES.EVENT && typeof detail.channel === "string"
  }

  private handleResult(detail: NativeRpcResultEnvelope): void {
    const pending = this.pending.get(detail.id)
    if (!pending) return

    clearTimeout(pending.timeout)
    this.pending.delete(detail.id)

    if (detail.ok) {
      pending.resolve(detail.data)
    } else {
      pending.reject(errorFromPayload(detail.error))
    }
  }

  private handleEvent(detail: NativeRpcEventEnvelope): void {
    for (const callback of this.handlers.get(detail.channel) ?? []) {
      callback(detail.data ?? {}, detail)
    }
  }

  private rejectPending(requestId: string, error: Error): void {
    const pending = this.pending.get(requestId)
    if (!pending) return

    clearTimeout(pending.timeout)
    this.pending.delete(requestId)
    pending.reject(error)
  }
}
